import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button, Stack } from "@mui/material";
import { CancelButton } from "../../../shared/CancelButton";
import { useCustomQuestionsByCustomer } from "../../../data/CustomWorkflowQuestion/useCustomQuestionsByCustomer";
import { ReusableCustomQuestionFields } from "./ReusableCustomQuestionFields";
import { ReusableCustomQuestionField } from "../../../shared/interfaces/ReusableCustomQuestionField";

interface Props {
  customerId: number;
  open: boolean;
  canEditWorkflow?: boolean;
  onClose: () => void;
  selectedQuestions?: ReusableCustomQuestionField[];
  onApplySelection: (selectedQuestions: ReusableCustomQuestionField[]) => void;
}

export function ReusableQuestionFieldSelectionDialog(props: Props) {
  const { t } = useTranslation();
  const { customerId, open, canEditWorkflow, onClose } = props;
  const [editedSelectedQuestions, setEditedSelectedQuestions] = useState(props.selectedQuestions || []);

  const customQuestions = useCustomQuestionsByCustomer(customerId);

  function handleSelectionChange(questionField: ReusableCustomQuestionField) {
    toggleSelected(questionField);
  }

  function handleApplySelectionChange() {
    props.onApplySelection(editedSelectedQuestions);
    onClose();
  }

  function toggleSelected(customQuestionField: ReusableCustomQuestionField) {
    const questionIsSelected = editedSelectedQuestions?.some(
      (selectedQuestion) => selectedQuestion.id === customQuestionField.id,
    );

    if (questionIsSelected)
      setEditedSelectedQuestions((prevSelectedQuestions) =>
        prevSelectedQuestions?.filter((q) => q.id !== customQuestionField.id),
      );
    else setEditedSelectedQuestions((prevSelectedQuestions) => [...(prevSelectedQuestions || []), customQuestionField]);
  }

  const header = <>{t("customQuestionLibrary.selectQuestions")}</>;

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} marginTop={0}>
      <Button variant="contained" onClick={handleApplySelectionChange}>
        {!props.selectedQuestions || props.selectedQuestions.length === 0
          ? t("customQuestionLibrary.addSelectedQuestions")
          : t("customQuestionLibrary.applySelection")}
      </Button>
      <CancelButton onClick={onClose} />
    </Stack>
  );

  return (
    <Dialog
      header={header}
      visible={open}
      onHide={onClose}
      footer={footer}
      breakpoints={{ "1300px": "90vw" }}
      style={{ width: "50vw", height: "90vh" }}
    >
      <ReusableCustomQuestionFields
        displayMode={"select"}
        selectedQuestions={editedSelectedQuestions}
        onSelectionChange={handleSelectionChange}
        customQuestionFields={customQuestions.data}
        customerId={customerId}
        canEditWorkflow={canEditWorkflow}
      />
    </Dialog>
  );
}
